import { FunctionalComponent, h } from 'preact';
import { useCallback, useLayoutEffect, useState } from 'preact/hooks';
import {
  useCheckoutContext,
  useCheckoutStore,
  useSelector,
  SceneLoaderProvider,
} from '@primer-io/shared-library/contexts';
import { Scene } from '../../enums/Checkout';
import CheckoutScene from '../../scenes/CheckoutScene';
import LoadingScene from '../../scenes/LoadingScene';
import PaymentMethodSuccessScene from '../../scenes/success/PaymentMethodSuccessScene';
import VaultManagerScene from '../../scenes/VaultManagerScene';
import { SceneStage } from '../../store/BaseStore';
import CheckSuccessScene from '../../scenes/success/CheckSuccessScene';

const SceneLoaders = {
  [Scene.LOADING]: async () => LoadingScene,
  [Scene.CHOOSE_PAYMENT_METHOD]: async () => CheckoutScene,
  [Scene.MANAGE_PAYMENT_METHODS]: async () => VaultManagerScene,
  [Scene.SUCCESS_PAYMENT_METHOD]: async () => PaymentMethodSuccessScene,
  [Scene.SUCCESS_CHECK]: async () => CheckSuccessScene,
};

type Props = {
  scene: string;
};

const SceneLoader: FunctionalComponent<Props> = ({ scene }) => {
  const { transitions, context } = useCheckoutContext();
  const store = useCheckoutStore();
  const sceneStage = useSelector(
    (s) => s.sceneStates[scene]?.stage,
  ) as SceneStage;

  const [SceneComponent, setSceneComponent] = useState({
    Component: null,
  });

  useLayoutEffect(() => {
    (async () => {
      let Component;
      let sceneRootId = scene;

      // Find component
      const pack = (
        await Promise.all(
          context?.packages.map(async (p) => {
            const declaration = await p.getDeclaration();
            const hasScene = declaration?.getScenePath?.(scene);
            return hasScene && p;
          }) ?? [],
        )
      ).find((p) => !!p);

      if (!pack) {
        Component = await SceneLoaders[scene]();
      } else {
        const scenePath = (await pack.getDeclaration())?.getScenePath?.(scene);
        if (scenePath) {
          const module = pack.getModule(scenePath);
          Component = await module.import();
          sceneRootId = (await module.import('sceneRootId')) as string;
        }
      }

      setSceneComponent({ Component });

      if (sceneStage === SceneStage.Entering) {
        await transitions.onEnter(sceneRootId as Scene);
        store.handleSceneEntered(scene);
        store.setErrorMessage(null);
      } else if (sceneStage === SceneStage.Exiting) {
        await transitions.onExit(sceneRootId as Scene);
        store.handleSceneExited(scene);
      }
    })();
  }, [sceneStage]);

  const setIsMounted = useCallback(() => {
    store.handleSceneMounted(scene);
  }, []);

  const Component = SceneComponent.Component as FunctionalComponent | null;

  return (
    <SceneLoaderProvider value={{ setIsMounted }}>
      {Component && <Component />}
    </SceneLoaderProvider>
  );
};

export default SceneLoader;
